package com.siriusxm.pia.views

import androidx.compose.runtime.Composable
import com.siriusxm.pia.Application
import com.siriusxm.pia.ApplicationDescriptor
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object HomeStyles : StyleSheet() {
    val applicationList by style {
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
    }

    val application by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(14.px)
        padding(1.cssRem)
        border(bottom = 1.px, color = SXMUI.borderDividerDefault.value())
        cursor("pointer")

        type("h4") style {
            fontSize((1.2).em)
            fontWeight(700)
            margin(0.px, 0.px, 4.px, 0.px)
        }

        self + hover style {
            backgroundColor(SXMUI.borderDividerDefault.value())
        }
    }

    // Decorative application icon shown at the start of each list row.
    val applicationIcon by style {
        property("flex", "0 0 auto")
        width(32.px)
        height(32.px)
    }

    // Wraps the name/description so they stack beside the icon.
    val applicationBody by style {
        property("flex", "1 1 auto")
        property("min-width", "0")
    }

    val applicationDescription by style {
        color(SXMUI.subtleTextColor.value())
        fontSize((0.9).cssRem)
    }
    val homeBoxes by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        gap(20.px)

        child(self, type("div")).style {
            flex(1, 1, 400.px)
            minWidth(400.px)
            maxWidth(600.px)
        }
    }

    val homeBox by style {
    }
}

/**
 * Renders a single application entry in the applications list.
 * When [image] is provided it is shown as a decorative icon at the start of the row.
 * External applications (URLs starting with http) show an external link icon
 * and open in a new tab when clicked.
 *
 * @param application The main Application instance for navigation
 * @param name The display name of the application
 * @param description Optional description text shown below the name
 * @param path The navigation path or external URL
 * @param image Optional path to an icon/logo image shown before the name
 */
@Composable
fun application(application: Application, name: String, description: String?, path: String, image: String? = null) {
    val isExternal = path.startsWith("http")
    Div({
        classes(HomeStyles.application)
        if (!isExternal) {
            onClick {
                it.preventDefault()
                application.navigate(path)
            }
        }
    }) {
        if (image != null) {
            Img(src = image, alt = name) { classes(HomeStyles.applicationIcon) }
        }
        Div({ classes(HomeStyles.applicationBody) }) {
            A(if (isExternal) path else "/#${path}", {
                if (isExternal) {
                    target(ATarget.Blank)
                }
            }) {
                H4 {
                    Text(name)
                    if (isExternal) {
                        Span({
                            style {
                                marginLeft(8.px)
                                color(SXMUI.subtleTextColor.value())
                            }
                            title("Opens in new tab")
                        }) {
                            svgIcon(SVGIcons.externalLink) {
                                size = 16.px
                            }
                        }
                    }
                }
            }
            if (description != null) {
                Div({ classes(HomeStyles.applicationDescription) }) {
                    Text(description)
                }
            }
        }
    }
}


/**
 * Renders the home dashboard with applications list and user favorites.
 *
 * The dashboard displays:
 * - A list of internal applications the user has access to
 * - A list of external applications (if any)
 * - The user's favorited entities with quick access links
 *
 * @param application The main Application instance
 */
@Composable
fun homeDashboard(application: Application) {
    serviceView {
        serviceContentView {
            Div({
                classes(HomeStyles.homeBoxes)
            }) {
                val (internalApps, externalApps) =
                    application.configuration.applications.orEmpty().partition {
                        it.internal()
                    }
                Div({
                    classes(HomeStyles.homeBox)
                }) {
                    box({
                        title = "Applications"
                        paddedContent = false
                    }) {
                        applicationsList(
                            application,
                            internalApps
                        )
                    }
                }

                Div({
                    classes(HomeStyles.homeBox)
                }) {
                    if (externalApps.isNotEmpty()) {
                        box({
                            title = "External Applications"
                            paddedContent = false
                        }) {
                            applicationsList(application, externalApps)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun applicationsList(application: Application, applications: List<ApplicationDescriptor>) {
    Div({ classes(HomeStyles.applicationList) }) {
        applications.forEach {
            val allowed = application.hasApplicationAccess(it)
            if (allowed) {
                application(application, it.name, it.description, it.path, it.image)
            }
        }
    }
}